import { reactive } from "vue";

const DISMISS_KEY = "appUpdateBannerDismissed";
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const appUpdateState = reactive({
  visible: false,
  message: "A new version is available.",
  reason: "version",
});

const EMBEDDED_BUILD_ID = process.env.VUE_APP_BUILD_ID || "";

export function isChunkLoadError(error) {
  const msg = error?.message || String(error ?? "");
  return (
    error?.name === "ChunkLoadError" ||
    /Loading chunk [\d\w_-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)
  );
}

export function notifyAppUpdate(options = {}) {
  const reason = options.reason || "version";
  if (reason === "version" && sessionStorage.getItem(DISMISS_KEY) === "1") {
    return;
  }
  appUpdateState.reason = reason;
  appUpdateState.message =
    options.message ||
    (reason === "chunk"
      ? "This page failed to load after an update—refresh to continue."
      : "A new version is available.");
  appUpdateState.visible = true;
}

export function dismissAppUpdateBanner() {
  sessionStorage.setItem(DISMISS_KEY, "1");
  appUpdateState.visible = false;
}

export function refreshApp() {
  window.location.reload();
}

export async function checkForAppUpdate() {
  if (!EMBEDDED_BUILD_ID || process.env.NODE_ENV !== "production") {
    return;
  }
  try {
    const res = await fetch(`/build-meta.json?_=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) {
      return;
    }
    const data = await res.json();
    if (data.buildId && data.buildId !== EMBEDDED_BUILD_ID) {
      notifyAppUpdate({ reason: "version" });
    }
  } catch {
    // ignore transient network errors
  }
}

export function startAppUpdateCheck() {
  if (process.env.NODE_ENV !== "production") {
    return;
  }
  checkForAppUpdate();
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      checkForAppUpdate();
    }
  });
  window.setInterval(checkForAppUpdate, POLL_INTERVAL_MS);
}

export function registerChunkLoadHandlers(router) {
  if (process.env.NODE_ENV !== "production") {
    return;
  }
  router.onError((error) => {
    if (isChunkLoadError(error)) {
      notifyAppUpdate({ reason: "chunk" });
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      notifyAppUpdate({ reason: "chunk" });
    }
  });
}
